import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ArrowRightIcon } from '@phosphor-icons/react';
import SelectDropdown, {
  SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import {
  FieldMappings,
  CompositeMapping,
  MappingValue,
  MappingMatchSource,
  SimpleMappingValue,
  SplitMappingValue,
} from 'src/api/spreadsheet/workflow';
import { SplitConfigurationUI } from './SplitConfigurationUI';
import { Button } from '@/components/ui/button';
import { DatePicker } from 'src/components/common/date-pickers/date-picker';

// Helper to normalize mapping value
function normalizeMappingValue(input: string | MappingValue): MappingValue {
  if (typeof input === 'string') {
    return { type: 'simple', value: input };
  }
  return input;
}

// Helper to get match source from mapping value
function getMatchSource(
  input: string | MappingValue | undefined,
): MappingMatchSource | undefined {
  if (!input || typeof input === 'string') return undefined;
  return input.matchSource;
}

// Match source badge styles and labels
const matchSourceConfig: Record<
  MappingMatchSource,
  { label: string; className: string; title: string }
> = {
  direct: {
    label: 'Direct',
    className: 'bg-green-100 text-green-800 border-green-300',
    title: 'Exact name match',
  },
  alias: {
    label: 'Alias',
    className: 'bg-blue-100 text-blue-800 border-blue-300',
    title: 'Matched via saved alias',
  },
  ai: {
    label: 'AI',
    className: 'bg-purple-100 text-purple-800 border-purple-300',
    title: 'AI determined this mapping',
  },
  user: {
    label: 'User',
    className: 'bg-amber-100 text-amber-800 border-amber-300',
    title: 'Manually set by user',
  },
};

// Component to display match source badge
function MatchSourceBadge({ source }: { source?: MappingMatchSource }) {
  if (!source) return null;
  const config = matchSourceConfig[source];
  return (
    <Badge
      variant="outline"
      className={`text-xs ml-2 ${config.className}`}
      title={config.title}
    >
      {config.label}
    </Badge>
  );
}

// Date format conversion utilities for date mappings
function parseISODate(isoString: string): Date | undefined {
  if (!isoString) return undefined;
  const isoRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
  if (!isoRegex.test(isoString)) return undefined;
  const date = new Date(isoString + 'T00:00:00');
  return isNaN(date.getTime()) ? undefined : date;
}

function formatDateToISO(date: Date | undefined): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Lightweight, memoized editor for mapped values to avoid re-rendering whole rows
interface MappingValueEditorProps {
  rowKey: string;
  mappedValue: string | MappingValue;
  isDropdown: boolean;
  options: SelectDropdownOption[];
  loading: boolean;
  placeholder: string;
  onUpdate: (newValue: string | MappingValue) => void;
  // Optional text input behavior
  inputType?: 'text' | 'date';
  externalError?: boolean;
  externalHelperText?: string;
  debounceMs?: number;
  supportsSplit?: boolean; // Enable split UI for this editor
  disabled?: boolean;
}

const MappingValueEditor: React.FC<MappingValueEditorProps> = React.memo(
  ({
    mappedValue,
    isDropdown,
    options,
    loading,
    placeholder,
    onUpdate,
    inputType = 'text',
    externalError,
    externalHelperText,
    debounceMs = 250,
    supportsSplit = true,
    disabled = false,
  }) => {
    // Normalize the mapping value
    const normalized = React.useMemo(
      () => normalizeMappingValue(mappedValue),
      [mappedValue],
    );

    // Track current mode (simple vs split)
    const [mode, setMode] = React.useState<'simple' | 'split'>(normalized.type);

    // Keep mode in sync with external changes
    React.useEffect(() => {
      setMode(normalized.type);
    }, [normalized.type]);

    const findSelectedOptionLocal = (
      value: string,
      opts: SelectDropdownOption[],
    ): SelectDropdownOption | null => {
      if (!value) return null;
      const normalized = String(value).trim().toLowerCase();
      return (
        opts.find((o) => o.display.trim().toLowerCase() === normalized) || null
      );
    };

    // Local state is used only for the text input path, but hooks must be unconditional
    const [localValue, setLocalValue] = React.useState<string>(() => {
      if (normalized.type === 'simple') {
        return normalized.value || '';
      }
      return '';
    });

    // Track the last value we synced from parent to detect user edits
    const lastSyncedValueRef = React.useRef<string>(
      normalized.type === 'simple' ? normalized.value || '' : '',
    );

    // Keep local input in sync with external mappedValue for simple mode
    React.useEffect(() => {
      if (normalized.type === 'simple') {
        setLocalValue(normalized.value || '');
        lastSyncedValueRef.current = normalized.value || '';
      }
    }, [normalized]);

    // Debounce updates to parent only when using text input AND value actually changed
    React.useEffect(() => {
      if (isDropdown || mode === 'split') return;
      // Only trigger update if value changed from last synced value (user edited it)
      if (localValue === lastSyncedValueRef.current) return;
      const id = setTimeout(() => {
        onUpdate(localValue);
        lastSyncedValueRef.current = localValue;
      }, debounceMs);
      return () => clearTimeout(id);
    }, [localValue, onUpdate, debounceMs, isDropdown, mode]);

    // Handle switching to split mode
    const handleConfigureSplit = () => {
      const initialSplit: SplitMappingValue = {
        type: 'split',
        splits: [
          { value: '', percentage: 50 },
          { value: '', percentage: 50 },
        ],
      };
      setMode('split');
      onUpdate(initialSplit);
    };

    // Handle switching back to simple mode
    const handleSwitchToSimple = () => {
      const simpleValue: SimpleMappingValue = {
        type: 'simple',
        value: '',
      };
      setMode('simple');
      onUpdate(simpleValue);
    };

    // Handle split configuration changes
    const handleSplitChange = (
      splits: Array<{ value: string; percentage: number }>,
    ) => {
      const splitValue: SplitMappingValue = {
        type: 'split',
        splits,
      };
      onUpdate(splitValue);
    };

    // Compute dropdown-specific values for simple mode
    const selected = React.useMemo(() => {
      if (normalized.type === 'simple') {
        return findSelectedOptionLocal(normalized.value, options);
      }
      return null;
    }, [normalized, options]);

    const showError =
      mode === 'simple' &&
      isDropdown &&
      !loading &&
      options.length > 0 &&
      !selected;

    // Compute text input specific validation
    const isoRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const inReasonableRange = (val: string): boolean => {
      if (!isoRegex.test(val)) return false;
      const [y, m, d] = val.split('-').map((s) => parseInt(s, 10));
      const yearOk = y >= 1990 && y <= 2059;
      const monthOk = m >= 1 && m <= 12;
      const dayOk = d >= 1 && d <= 31;
      if (!(yearOk && monthOk && dayOk)) return false;
      const dt = new Date(val);
      return !isNaN(dt.getTime());
    };

    const isDateInvalid =
      inputType === 'date' &&
      localValue !== '' &&
      !inReasonableRange(localValue);
    const computedError =
      externalError ??
      (inputType === 'date' ? isDateInvalid : localValue === '');
    const computedHelper =
      externalHelperText ??
      (inputType === 'date'
        ? isDateInvalid
          ? 'Use YYYY-MM-DD, year 1990–2059'
          : undefined
        : localValue === ''
          ? 'Required'
          : undefined);

    // Render split configuration UI
    if (mode === 'split' && isDropdown && supportsSplit) {
      return (
        <SplitConfigurationUI
          splits={normalized.type === 'split' ? normalized.splits : []}
          options={options}
          onChange={handleSplitChange}
          onSwitchToSimple={handleSwitchToSimple}
          loading={loading}
          disabled={disabled}
        />
      );
    }

    // Render simple mode dropdown with "Configure Split" button
    if (isDropdown) {
      return (
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <SelectDropdown
              options={options}
              value={selected}
              onChange={(selectedOption) => {
                const simpleValue: SimpleMappingValue = {
                  type: 'simple',
                  value: selectedOption?.display || '',
                };
                onUpdate(simpleValue);
              }}
              placeholder={placeholder}
              size="small"
              disabled={disabled || loading}
              error={showError}
              helperText={showError ? 'Select a valid option' : undefined}
              fullWidth={true}
            />
          </div>
          {supportsSplit && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleConfigureSplit}
              disabled={disabled}
              className="text-xs flex-shrink-0 whitespace-nowrap"
            >
              ⚡ Split
            </Button>
          )}
        </div>
      );
    }

    // Render date picker for date inputs
    if (inputType === 'date') {
      const dateValue = parseISODate(localValue);
      const hasError = localValue !== '' && !dateValue;

      return (
        <div className="space-y-1">
          <DatePicker
            value={dateValue}
            placeholder={placeholder}
            onSelect={(date: Date | undefined) => {
              const isoString = formatDateToISO(date);
              setLocalValue(isoString);
              onUpdate(isoString);
            }}
            className="w-full"
          />
          {hasError && (
            <p className="text-sm text-red-500">
              Invalid date format. Use YYYY-MM-DD, year 1990–2059
            </p>
          )}
        </div>
      );
    }

    // Render text input for non-dropdown, non-date fields
    return (
      <div className="space-y-1">
        <Input
          value={localValue}
          onChange={(e) => {
            setLocalValue(e.target.value);
          }}
          placeholder={placeholder}
          type={inputType}
          disabled={disabled}
          className={computedError ? 'border-red-500' : ''}
        />
        {computedHelper && (
          <p
            className={`text-sm ${
              computedError ? 'text-red-500' : 'text-gray-500'
            }`}
          >
            {computedHelper}
          </p>
        )}
      </div>
    );
  },
);

MappingValueEditor.displayName = 'MappingValueEditor';

interface MappingTypeConfig {
  displayName: string;
  options?: SelectDropdownOption[];
  isLoading?: boolean;
  placeholder?: string;
}

interface FieldMappingTableProps {
  mappings: FieldMappings;
  setMappings: React.Dispatch<React.SetStateAction<FieldMappings>>;
  mappingTypeConfigs?: Partial<Record<keyof FieldMappings, MappingTypeConfig>>;
  disabled?: boolean;
}

interface MappingRowData {
  key: string;
  columns: string[];
  mappedValue: string | MappingValue;
  isAsterisk?: boolean;
  asteriskLabel?: string;
}

const FieldMappingTable: React.FC<FieldMappingTableProps> = ({
  mappings,
  setMappings,
  mappingTypeConfigs = {},
  disabled = false,
}) => {
  // Track expanded state for each section
  const [expandedSections, setExpandedSections] = React.useState<
    Set<keyof FieldMappings>
  >(new Set());

  const toggleSection = (type: keyof FieldMappings) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // Merge provided configs with defaults
  const finalConfigs = useMemo(() => {
    // Default configurations for mapping types
    const defaultConfigs: Record<keyof FieldMappings, MappingTypeConfig> = {
      divisionMappings: {
        displayName: 'Division Mappings',
        placeholder: 'Select division...',
      },
      productMappings: {
        displayName: 'Product Mappings',
        placeholder: 'Select product...',
      },
      dateMappings: {
        displayName: 'Date Mappings',
        placeholder: 'Enter date format...',
      },
    };

    const result: Record<keyof FieldMappings, MappingTypeConfig> = {
      ...defaultConfigs,
    };

    Object.entries(mappingTypeConfigs).forEach(([key, config]) => {
      if (config) {
        result[key as keyof FieldMappings] = {
          ...result[key as keyof FieldMappings],
          ...config,
        };
      }
    });

    return result;
  }, [mappingTypeConfigs]);
  // Parse a composite mapping key into its component columns
  const parseCompositeKey = (key: string, separator = '|||'): string[] => {
    return key.split(separator);
  };

  // Convert CompositeMapping to display rows
  const convertToTableRows = (mapping: CompositeMapping): MappingRowData[] => {
    const keys = Object.keys(mapping.mappings || {});
    const hasNonAsterisk = keys.some((k) => k !== '*');
    const asteriskLabel = hasNonAsterisk ? 'Default' : 'All';

    return Object.entries(mapping.mappings).map(
      ([key, value]: [string, string | MappingValue]) => {
        const isAsterisk = key === '*';
        const columns = isAsterisk
          ? [asteriskLabel]
          : parseCompositeKey(key, mapping.separator);
        return {
          key,
          columns,
          mappedValue: value,
          isAsterisk,
          asteriskLabel: isAsterisk ? asteriskLabel : undefined,
        } as MappingRowData;
      },
    );
  };

  // Get display name for mapping type
  const getMappingDisplayName = (type: keyof FieldMappings): string => {
    return finalConfigs[type].displayName;
  };

  // Determine if we should use dropdown for this mapping type
  const shouldUseDropdown = (type: keyof FieldMappings): boolean => {
    const options = finalConfigs[type].options;
    return !!options && options.length > 0;
  };

  // Get dropdown options for mapping type
  const getDropdownOptions = (
    type: keyof FieldMappings,
  ): SelectDropdownOption[] => {
    return finalConfigs[type].options || [];
  };

  // Get placeholder for mapping type
  const getPlaceholder = (type: keyof FieldMappings): string => {
    return finalConfigs[type].placeholder || 'Enter value...';
  };

  // Check if mapping type is loading
  const isTypeLoading = (type: keyof FieldMappings): boolean => {
    return finalConfigs[type].isLoading || false;
  };

  // Find selected option for a mapped value
  const findSelectedOption = (
    mappedValue: string | MappingValue,
    options: SelectDropdownOption[],
  ): SelectDropdownOption | null => {
    if (!mappedValue) return null;

    // Normalize MappingValue to string
    let valueStr: string;
    if (typeof mappedValue === 'string') {
      valueStr = mappedValue;
    } else if (mappedValue.type === 'simple') {
      valueStr = mappedValue.value;
    } else {
      // Split mappings can't be represented as a single option
      return null;
    }

    const normalized = valueStr.trim().toLowerCase();
    return (
      options.find(
        (option) => option.display.trim().toLowerCase() === normalized,
      ) || null
    );
  };

  // Validate a split mapping
  const isValidSplitMapping = (
    split: SplitMappingValue,
    options: SelectDropdownOption[],
  ): boolean => {
    // Check if splits array is not empty
    if (!split.splits || split.splits.length === 0) return false;

    // Check if percentages sum to 100 (with tolerance: accept 99.90 - 100.10)
    const total = split.splits.reduce((sum, s) => sum + s.percentage, 0);
    if (Math.abs(total - 100) > 0.1) return false;

    // Check each split has valid percentage and value
    for (const s of split.splits) {
      if (s.percentage <= 0 || s.percentage > 100) return false;
      if (!s.value || s.value.trim() === '') return false;

      // Check if value exists in options
      const normalized = s.value.trim().toLowerCase();
      const optionExists = options.some(
        (o) => o.display.trim().toLowerCase() === normalized,
      );
      if (!optionExists) return false;
    }

    // Check for duplicate values (case-sensitive)
    const values = split.splits.map((s) => s.value.trim());
    if (new Set(values).size !== values.length) return false;

    return true;
  };

  // Render individual mapping section
  const renderMappingSection = (
    type: keyof FieldMappings,
    mapping: CompositeMapping,
  ) => {
    const allRows = convertToTableRows(mapping);
    const isExpanded = expandedSections.has(type);
    const hasManyRows = allRows.length > 6;
    const rows = hasManyRows && !isExpanded ? allRows.slice(0, 6) : allRows;
    const remainingCount = allRows.length - 6;

    // Count rows needing attention for the section title
    const invalidRowsCount = allRows.reduce((count, row) => {
      const options = getDropdownOptions(type);
      const loading = isTypeLoading(type);
      const normalized = normalizeMappingValue(row.mappedValue);

      // Check for "unknown" values
      const isUnknown =
        normalized.type === 'simple' &&
        normalized.value.trim().toLowerCase() === 'unknown';

      // Check for empty text inputs (non-dropdown fields)
      const isEmptyText =
        !shouldUseDropdown(type) &&
        normalized.type === 'simple' &&
        normalized.value === '';

      // Check for invalid dropdown selections
      let isInvalidDropdown = false;
      if (shouldUseDropdown(type) && !loading && options.length > 0) {
        if (normalized.type === 'simple') {
          // Simple mapping: check if value exists in options
          isInvalidDropdown = !findSelectedOption(row.mappedValue, options);
        } else if (normalized.type === 'split') {
          // Split mapping: validate the split configuration
          isInvalidDropdown = !isValidSplitMapping(normalized, options);
        }
      }

      return count + (isUnknown || isEmptyText || isInvalidDropdown ? 1 : 0);
    }, 0);

    if (rows.length === 0) {
      return null; // Don't show empty sections
    }

    return (
      <AccordionItem key={type} value={type}>
        <AccordionTrigger>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">
              {getMappingDisplayName(type)}
            </h3>
            <Badge variant="outline" className="text-blue-600 border-blue-600">
              {allRows.length} mapping{allRows.length !== 1 ? 's' : ''}
            </Badge>
            {invalidRowsCount > 0 && (
              <Badge variant="warning">
                {invalidRowsCount} needs attention
              </Badge>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="border rounded-lg overflow-hidden">
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  {/* Dynamic column headers based on sourceColumnNames */}
                  {mapping.sourceColumnNames.length > 0 ? (
                    mapping.sourceColumnNames.map(
                      (columnName: string, index: number) => (
                        <TableHead key={index}>{columnName}</TableHead>
                      ),
                    )
                  ) : (
                    <TableHead>Source Data</TableHead>
                  )}
                  {/* Empty header cell for mapping arrow */}
                  <TableHead className="w-14 text-center">
                    <ArrowRightIcon className="w-4 h-4 text-gray-400" />
                  </TableHead>
                  <TableHead>Mapped Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const options = getDropdownOptions(type);
                  const loading = isTypeLoading(type);
                  const normalized = normalizeMappingValue(row.mappedValue);

                  // Check for "unknown" values
                  const isUnknownRow =
                    normalized.type === 'simple' &&
                    normalized.value.trim().toLowerCase() === 'unknown';

                  // Check for empty text inputs
                  const isEmptyTextRow =
                    !shouldUseDropdown(type) &&
                    normalized.type === 'simple' &&
                    normalized.value === '';

                  // Check for invalid dropdown selections
                  let isInvalidDropdownRow = false;
                  if (
                    shouldUseDropdown(type) &&
                    !loading &&
                    options.length > 0
                  ) {
                    if (normalized.type === 'simple') {
                      isInvalidDropdownRow = !findSelectedOption(
                        row.mappedValue,
                        options,
                      );
                    } else if (normalized.type === 'split') {
                      isInvalidDropdownRow = !isValidSplitMapping(
                        normalized,
                        options,
                      );
                    }
                  }

                  const isInvalidRow =
                    isUnknownRow || isEmptyTextRow || isInvalidDropdownRow;
                  return (
                    <TableRow
                      key={row.key}
                      className={`
                        hover:bg-gray-50 transition-colors
                        ${
                          isInvalidRow
                            ? 'bg-red-50 relative border border-red-200 rounded hover:bg-red-100'
                            : ''
                        }
                      `}
                    >
                      {/* Display each column value */}
                      {mapping.sourceColumnNames.length > 0 ||
                      row.isAsterisk ? (
                        row.isAsterisk &&
                        mapping.sourceColumnNames.length > 0 ? (
                          // Wildcard row - span all source columns
                          <TableCell
                            colSpan={mapping.sourceColumnNames.length}
                            className="text-center"
                          >
                            <Badge variant="secondary" className="text-sm">
                              {row.columns[0]}
                            </Badge>
                          </TableCell>
                        ) : (
                          // Regular row - map each column
                          row.columns.map((columnValue, colIndex) => (
                            <TableCell key={colIndex}>
                              {columnValue === '' ? (
                                <Badge
                                  variant="outline"
                                  className="text-xs text-amber-700 bg-amber-50 border-amber-300"
                                >
                                  (empty)
                                </Badge>
                              ) : (
                                <span className="text-sm text-gray-900">
                                  {columnValue}
                                </span>
                              )}
                            </TableCell>
                          ))
                        )
                      ) : (
                        <TableCell>
                          <span className="text-sm text-gray-500 italic">
                            (No source data - AI fallback)
                          </span>
                        </TableCell>
                      )}
                      {/* Mapping arrow column */}
                      <TableCell className="w-14 text-center">
                        <ArrowRightIcon className="w-4 h-4 text-gray-400" />
                      </TableCell>
                      {/* Editable mapped value */}
                      <TableCell>
                        <div className="flex items-center">
                          <MappingValueEditor
                            rowKey={row.key}
                            mappedValue={row.mappedValue}
                            isDropdown={shouldUseDropdown(type)}
                            options={getDropdownOptions(type)}
                            loading={isTypeLoading(type)}
                            placeholder={getPlaceholder(type)}
                            inputType={
                              type === 'dateMappings' ? 'date' : 'text'
                            }
                            externalError={
                              type === 'dateMappings' &&
                              !/^\d{4}-\d{2}-\d{2}$/.test(
                                String(row.mappedValue || ''),
                              )
                            }
                            externalHelperText={
                              type === 'dateMappings' &&
                              !/^\d{4}-\d{2}-\d{2}$/.test(
                                String(row.mappedValue || ''),
                              )
                                ? 'Use ISO 8601 date (YYYY-MM-DD)'
                                : undefined
                            }
                            disabled={disabled}
                            onUpdate={(newValue: string | MappingValue) => {
                              setMappings((prev) => {
                                const currentMapping = prev[type];
                                if (!currentMapping) return prev;
                                // Mark as user-modified when value changes
                                let valueWithSource: string | MappingValue;
                                if (typeof newValue === 'string') {
                                  valueWithSource = {
                                    type: 'simple',
                                    value: newValue,
                                    matchSource: 'user',
                                  };
                                } else {
                                  valueWithSource = {
                                    ...newValue,
                                    matchSource: 'user',
                                  };
                                }
                                const updated = {
                                  ...prev,
                                  [type]: {
                                    ...currentMapping,
                                    mappings: {
                                      ...currentMapping.mappings,
                                      [row.key]: valueWithSource,
                                    },
                                  },
                                } as FieldMappings;
                                return updated;
                              });
                            }}
                          />
                          <MatchSourceBadge
                            source={getMatchSource(row.mappedValue)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {hasManyRows && (
              <div className="border-t bg-gray-50 px-4 py-3 flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSection(type)}
                  className="text-sm"
                >
                  {isExpanded ? `Show less` : `Show ${remainingCount} more`}
                </Button>
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  // Check if there are any mappings to display
  const hasAnyMappings = Object.values(mappings).some(
    (mapping: CompositeMapping) => Object.keys(mapping.mappings).length > 0,
  );

  if (!hasAnyMappings) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-gray-500">
          No field mappings found in the data.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-medium mb-1">Field Mappings</h2>
        <p className="text-sm text-gray-600">
          Review and edit the AI-generated field mappings. Each row represents
          unique values found in your data that will be mapped to standardized
          values. Empty cells in your source data are shown with an{' '}
          <Badge
            variant="outline"
            className="text-xs text-amber-700 bg-amber-50 border-amber-300 inline-flex"
          >
            (empty)
          </Badge>{' '}
          badge.
        </p>
      </div>

      <Accordion
        type="multiple"
        defaultValue={['divisionMappings', 'productMappings', 'dateMappings']}
        className="space-y-4"
      >
        {(Object.keys(mappings) as Array<keyof FieldMappings>).map((type) => {
          const mapping = mappings[type];
          return mapping ? renderMappingSection(type, mapping) : null;
        })}
      </Accordion>
    </div>
  );
};

export default FieldMappingTable;
